import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { aboutContent, faqData } from "@/mocks/about";

export default function AboutPage() {
  const [formState, setFormState] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formData, setFormData] = useState({ name: "", email: "", message: "", website_alt: "" });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const honeypot = (e.currentTarget.elements.namedItem("website_alt") as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState("success");
      return;
    }

    setFormState("loading");
    setFormError("");

    try {
      const formBody = new URLSearchParams(new FormData(e.currentTarget) as unknown as Record<string, string>);
      formBody.delete("website_alt");

      const res = await fetch("https://readdy.ai/api/form/d9otg1aftmtnfcfh0dr0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody.toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (!res.ok || !parsed || (parsed.code !== "OK")) {
        const serverMsg = (parsed && typeof parsed === "object" && "meta" in parsed && typeof parsed.meta === "object" && parsed.meta && "message" in parsed.meta)
          ? String((parsed.meta as Record<string, unknown>).message)
          : (parsed && typeof parsed === "object" && "message" in parsed ? String((parsed as Record<string, unknown>).message) : "");
        if (serverMsg && serverMsg.includes("spam")) {
          setFormError("提交出现异常，请稍后再试");
          setFormState("error");
          return;
        }
        setFormError(serverMsg || "提交失败，请稍后重试");
        setFormState("error");
        return;
      }

      setFormState("success");
      setFormData({ name: "", email: "", message: "", website_alt: "" });
    } catch {
      setFormError("网络错误，请检查网络后重试");
      setFormState("error");
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 哈哈漫画网 - 韩国漫画免费在线观看平台"
        description="哈哈漫画网成立于2023年，是国内领先的韩国漫画在线阅读平台。提供高清韩漫全集下拉式阅读服务，收录5000+优质漫画，每日更新200+作品。"
        keywords="哈哈漫画网,关于我们,韩国漫画平台,免费漫画网站,漫画阅读平台"
        canonicalUrl="https://hahacomic.com/about"
        ogType="website"
        schemaType="FAQPage"
        faqData={faqData}
      />
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: "关于我们" },
            ]}
          />

          <section className="mb-14">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              关于哈哈漫画网
            </h1>
            <p className="text-sm text-foreground-500 mb-8">{aboutContent.tagline}</p>
            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{aboutContent.introduction}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                {aboutContent.stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="text-2xl md:text-3xl font-bold text-primary-500" style={{ fontFamily: "var(--font-heading)" }}>
                      {stat.value}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              平台特色
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutContent.features.map((feat) => (
                <div key={feat.title} className="p-6 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                    <i className={`${feat.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{feat.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feat.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {aboutContent.reputation.map((item) => (
                <div key={item.username} className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={item.avatar}
                      alt={item.username}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{item.username}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: item.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                        ))}
                      </div>
                    </div>
                    <span className="ml-auto text-xs text-foreground-400">{item.date}</span>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.comment}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              常见问题
            </h2>
            <div className="space-y-3">
              {faqData.map((faq, i) => (
                <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h4 className="text-sm font-semibold text-foreground-900 pr-4">{faq.question}</h4>
                    <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            <div>
              <h2 className="text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                联系我们
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">电子邮箱</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">微信公众号</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">工作时间</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.workingHours}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">地址</p>
                    <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                给我们留言
              </h2>
              {formState === "success" ? (
                <div className="p-8 rounded-xl bg-background-100 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">留言已发送！</h3>
                  <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复您。</p>
                  <button
                    onClick={() => setFormState("idle")}
                    className="mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="sr-only" />
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>
                  {formState === "error" && formError && (
                    <p className="text-sm text-red-500">{formError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={formState === "loading"}
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {formState === "loading" ? "发送中..." : "发送留言"}
                  </button>
                </form>
              )}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}